import { css } from '@emotion/react';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';

const mobileQuery = `@media (max-width: ${breakpoints.small.max}px)`;

/** Space around the pages inside the scroll area, in px (also subtracted when fitting a page to the width). */
export const PDF_VIEWER_PADDING = 12;

export const fieldsetStyle = css({
  border: 'none',
  margin: 0,
  padding: 0,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
});

export const fieldErrorStyle = css({
  color: theme.colors.text.critical,
  margin: 0,
});

export const thumbnailButtonStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  width: '100%',
  padding: theme.spacing(1.5),
  textAlign: 'left',
  backgroundColor: theme.colors.neutral5,
  border: `1px solid ${theme.colors.neutral20}`,
  borderRadius: theme.borderRadius.medium,
  cursor: 'pointer',

  '&:hover, &:focus-visible': {
    borderColor: theme.colors.primary50,
  },

  '&[aria-invalid="true"]': {
    borderColor: theme.colors.critical50,
  },
});

export const thumbnailFrameStyle = css({
  flexShrink: 0,
  width: 96,
  overflow: 'hidden',
  backgroundColor: theme.colors.white,
  boxShadow: theme.shadows.light,
});

export const thumbnailTextStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  minWidth: 0,
});

export const statusBoxStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  backgroundColor: theme.colors.neutral5,
  borderRadius: theme.borderRadius.medium,
});

export const viewerStyle = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(2),
  // Grow with the widest page so a zoomed-in page scrolls to both edges; centred while it fits.
  boxSizing: 'border-box',
  width: 'max-content',
  minWidth: '100%',
  padding: PDF_VIEWER_PADDING,
  '&[data-pinching="true"]': {
    willChange: 'transform',
  },
});

export const pageWrapperStyle = css({
  position: 'relative',
  maxWidth: 'none',
  flexShrink: 0,
  backgroundColor: theme.colors.white,
  boxShadow: theme.shadows.light,
});

export const pageCanvasStyle = css({
  display: 'block',
  width: '100%',
  height: '100%',
});

export const pageLoaderStyle = css({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none',
});

export const pageErrorStyle = css({
  position: 'absolute',
  inset: 0,
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  backgroundColor: theme.colors.white,
  textAlign: 'center',
});

/** Keeps the extracted PDF text available to assistive technology without changing the visual page layout. */
export const documentTextAlternativeStyle = css({
  border: 0,
  clip: 'rect(0, 0, 0, 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px',
});

export const modalBodyStyle = css({
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  padding: 0,
  minHeight: 0,
});

export const scrollAreaStyle = css({
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
  overscrollBehavior: 'contain',
  backgroundColor: theme.colors.neutral5,
  // One-finger panning stays native scrolling; pinch zoom is handled by `usePdfZoom` instead of zooming the page.
  touchAction: 'pan-x pan-y',
});

export const toolbarStyle = css({
  display: 'grid',
  gridTemplateColumns: '1fr auto 1fr',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1),
  borderBottom: `1px solid ${theme.colors.neutral10}`,
});

export const zoomControlsStyle = css({
  gridColumn: 2,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
});

export const toolbarActionsStyle = css({
  gridColumn: 3,
  justifySelf: 'end',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
});

export const zoomValueStyle = css({
  minWidth: 48,
  textAlign: 'center',
  margin: 0,
});

export const footerStyle = css({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: theme.spacing(1),
  padding: theme.spacing(1.5, 2),
  borderTop: `1px solid ${theme.colors.neutral10}`,
  backgroundColor: theme.colors.white,

  // Phones: helper text on its own line, actions share the row below.
  [mobileQuery]: {
    padding: theme.spacing(1, 1.5),
    '& > button': { flex: '1 1 0', minWidth: 0 },
  },
});

export const footerHelperStyle = css({
  marginRight: 'auto',

  [mobileQuery]: {
    flexBasis: '100%',
    marginRight: 0,
    textAlign: 'center',
  },
});

export const footerNoticeStyle = css({
  width: '100%',
});

export const signatureModalBodyStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),

  [mobileQuery]: {
    '&&': { gap: theme.spacing(1.5) },
  },
});

export const placementStyle = css({
  position: 'absolute',
  // Let a finger that lands on a placed signature still scroll the document; only the active one is draggable.
  touchAction: 'pan-x pan-y',
  userSelect: 'none',
  outline: 'none',
  cursor: 'pointer',

  img: {
    display: 'block',
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
  },

  '&[data-active="true"]': {
    touchAction: 'none',
    cursor: 'move',
    outline: `2px dashed ${theme.colors.primary50}`,
    outlineOffset: 2,
  },

  '&:focus-visible': {
    outline: `2px solid ${theme.colors.primary50}`,
    outlineOffset: 2,
  },
});

export const placementStampStyle = css({
  position: 'absolute',
  left: 0,
  top: '100%',
  whiteSpace: 'nowrap',
  fontFamily: 'Helvetica, Arial, sans-serif',
  color: theme.colors.black,
  pointerEvents: 'none',
  lineHeight: 1.25,
});

export const placementControlsStyle = css({
  position: 'absolute',
  bottom: '100%',
  right: 0,
  display: 'flex',
  gap: theme.spacing(0.5),
  marginBottom: theme.spacing(0.5),
  padding: theme.spacing(0.25),
  backgroundColor: theme.colors.white,
  borderRadius: theme.borderRadius.small,
  boxShadow: theme.shadows.heavy,
});
